import type { ReportCollection, ReportErrors, ReportOverview, ReportPages, ReportPatterns, ReportPeople, ReportTeam, ReportTimeToFix, ReportTrend } from '../../lib/api';
import { comparison, figure, formatDuration } from './reportFrame';

/**
 * What each report means, in one sentence (16 Oct): written from the report's
 * own numbers by rule, never by a model, so it is instant, costs nothing and
 * can say nothing the figures do not. Each returns null when there is nothing
 * worth saying (the report's own empty note says it). A figure read short
 * keeps its "+" (`figure`), as everywhere else.
 */

const plural = (n: number | string, one: string, many = `${one}s`) => `${n} ${n === 1 || n === '1' ? one : many}`;
const percent = (part: number, whole: number) => Math.round((whole > 0 ? part / whole : 0) * 100);
/** An error message or address short enough to sit in a sentence. */
const clip = (text: string, max = 60) => (text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text);

export function overviewTakeaway(report: ReportOverview, against: string): string | null {
  const { current, previous } = report.opened;
  if (current === 0) return previous > 0 ? `No tickets were opened, against ${previous} in ${against}.` : null;
  const lead = `${plural(current, 'ticket')} opened, ${comparison(current, previous, against)}.`;
  const active = report.byProject.filter((row) => row.opened.current > 0);
  if (active.length < 2) return lead;
  const top = [...active].sort((a, b) => b.opened.current - a.opened.current)[0]!;
  return `${lead} ${top.project.name} opened the most: ${top.opened.current}, ${percent(top.opened.current, current)}% of them.`;
}

export function errorsTakeaway(report: ReportErrors): string | null {
  const top = report.groups[0];
  if (!top) return null;
  const share = percent(report.coverage.withErrors.current, report.coverage.captured);
  const where = top.projects.length === 1 ? ` in ${top.projects[0]!.name}` : top.projects.length > 1 ? ` across ${top.projects.length} projects` : '';
  const lead = `"${clip(top.example)}" was in the most tickets: ${figure(top.ticketCount, report.truncated)}${where}.`;
  return report.coverage.captured > 0 ? `${lead} ${share}% of tickets with DevTools data had errors.` : lead;
}

export function pagesTakeaway(report: ReportPages, rhythm?: ReportPatterns['rhythm']): string | null {
  const projects = report.byProject.filter((row) => row.current > 0);
  if (projects.length === 0) return null;
  const top = [...projects].sort((a, b) => b.current - a.current)[0]!;
  const short = report.truncated;
  const bugs = top.current > 0 && top.bugs / top.current > 0.5 ? ', most of them bugs' : '';
  const page = top.topPages[0];
  const lead = projects.length > 1
    ? `${top.project.name} drew the most tickets: ${figure(top.current, short)}${bugs}.`
    : `${plural(figure(top.current, short), 'ticket')} came in${bugs}.`;
  const chronic = (rhythm ?? []).filter((entry) => entry.rhythm === 'chronic');
  if (chronic.length > 0) return `${lead} ${chronic.length === 1 ? `${clip(chronic[0]!.page, 40)} keeps coming back` : `${chronic.length} pages keep coming back`}.`;
  return page ? `${lead} The busiest page is ${clip(page.page, 40)}, with ${figure(page.tickets, short)}.` : lead;
}

export function trendTakeaway(report: ReportTrend): string | null {
  if (report.weeks.length === 0) return null;
  const opened = report.weeks.reduce((sum, week) => sum + week.opened, 0);
  const closed = report.weeks.reduce((sum, week) => sum + week.closed, 0);
  if (opened === 0 && closed === 0) return null;
  if (closed === opened) return `Opened and closed kept pace: ${opened} of each.`;
  if (closed > opened) return `More closed than opened: ${closed} against ${opened}, so the open queue shrank by ${closed - opened}.`;
  return `More opened than closed: ${opened} against ${closed}, so the open queue grew by ${opened - closed}.`;
}

export function timeToFixTakeaway(report: ReportTimeToFix, against: string): string | null {
  const { current, previous } = report.overall;
  if (current.medianSeconds === null) return null;
  const compared = previous.medianSeconds === null || Math.round(current.medianSeconds / 60) === Math.round(previous.medianSeconds / 60)
    ? ''
    : `, ${current.medianSeconds < previous.medianSeconds ? 'faster' : 'slower'} than ${formatDuration(previous.medianSeconds)} in ${against}`;
  const lead = `A typical fix took ${formatDuration(current.medianSeconds)}${compared}.`;
  const timed = report.byProject.filter((row) => row.current.medianSeconds !== null);
  if (timed.length < 2) return lead;
  const slowest = [...timed].sort((a, b) => b.current.medianSeconds! - a.current.medianSeconds!)[0]!;
  return `${lead} ${slowest.project.name} was slowest, at ${formatDuration(slowest.current.medianSeconds)}.`;
}

export function teamTakeaway(report: ReportTeam): string | null {
  const active = report.people.filter((person) => person.activeDays > 0);
  if (active.length === 0) return null;
  const lead = `${active.length} of ${plural(report.people.length, 'person', 'people')} ${active.length === 1 ? 'was' : 'were'} active.`;
  const closer = [...active].sort((a, b) => b.closed - a.closed)[0]!;
  const agents = report.agents.length > 0 ? ` ${plural(report.agents.length, 'AI agent')} worked alongside them.` : '';
  return closer.closed > 0 ? `${lead} ${closer.name} closed the most: ${closer.closed}.${agents}` : `${lead}${agents}`;
}

export function peopleTakeaway(report: ReportPeople): string | null {
  const carrying = [...report.people].sort((a, b) => b.carrying - a.carrying)[0];
  if (!carrying || carrying.carrying === 0) return null;
  const total = report.people.reduce((sum, person) => sum + person.carrying, 0);
  return report.people.length > 1
    ? `${carrying.name} carries the most open tickets: ${carrying.carrying} of ${total}.`
    : `${carrying.name} carries ${plural(carrying.carrying, 'open ticket')}.`;
}

export function patternsSummary(report: ReportPatterns): string | null {
  // The whole counts, not the lists, which show only the first of each.
  const { chronic, sameElement } = report.counts;
  const short = report.truncated;
  const parts = [
    chronic > 0 ? `${plural(figure(chronic, short), 'page')} keep${chronic === 1 && !short ? 's' : ''} coming back` : null,
    sameElement > 0 ? `${plural(figure(sameElement, short), 'element')} drew three or more tickets` : null,
    report.reopens.total > 0 ? `${plural(figure(report.reopens.total, report.reopens.truncated), 'fix', 'fixes')} did not stick` : null,
  ].filter((part): part is string => part !== null);
  if (parts.length === 0) return 'No pattern stands out in this period.';
  const sentence = parts.length === 1 ? parts[0]! : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  return `${sentence[0]!.toUpperCase()}${sentence.slice(1)}.`;
}

/** The Tickets page in one line: how many, the change, and how many had errors. */
export function ticketsSummary(overview: ReportOverview | undefined, errors: ReportErrors | undefined, against: string): string | null {
  if (!overview) return null;
  const { current, previous } = overview.opened;
  if (current === 0 && previous === 0) return 'No tickets in this period or the one before.';
  const lead = `${plural(current, 'ticket')} opened, ${comparison(current, previous, against)}`;
  if (!errors || errors.coverage.captured === 0) return `${lead}.`;
  return `${lead}; ${percent(errors.coverage.withErrors.current, errors.coverage.captured)}% of those with DevTools data had errors.`;
}

/** The Team page in one line: closed, the typical fix, and which way the queue went. */
export function teamSummary(trend: ReportTrend | undefined, timeToFix: ReportTimeToFix | undefined): string | null {
  if (!trend) return null;
  const opened = trend.weeks.reduce((sum, week) => sum + week.opened, 0);
  const closed = trend.weeks.reduce((sum, week) => sum + week.closed, 0);
  if (opened === 0 && closed === 0) return 'Nothing opened or closed in this period.';
  const fix = timeToFix?.overall.current.medianSeconds;
  const queue = closed === opened ? 'the queue held steady' : closed > opened ? `the queue shrank by ${closed - opened}` : `the queue grew by ${opened - closed}`;
  return `${plural(closed, 'ticket')} closed${fix != null ? `, a typical fix in ${formatDuration(fix)}` : ''}; ${queue}.`;
}

/** Who a visitor is, in words: the name they gave, else their email, else that they gave neither. */
export function visitorLabel(visitor: ReportCollection['visitors']['list'][number]): string {
  return visitor.name ?? visitor.email ?? 'A visitor who left no email';
}

/**
 * Whether the visitor figures are lower bounds: when either read was cut. A
 * cut period read leaves out some of the period's reports; a cut history
 * leaves some reports no one's that could have named someone, and some
 * returning visitors unseen. Every visitor figure and sentence reads this one
 * rule (Codex review round 5: each deciding for itself missed a case).
 */
export const visitorsUncertain = (report: ReportCollection): boolean => report.truncated || report.visitors.historyTruncated;

/** Bug collection's sentence at the top of its tab (TOADO-964). */
export function collectionSummary(report: ReportCollection, against: string): string {
  const { current, previous } = report.opened;
  const short = report.truncated;
  if (current === 0) {
    return previous > 0
      ? `No reports came in through the bug collection widget, against ${figure(previous, short)} in ${against}.`
      : 'No reports came in through the bug collection widget in this period.';
  }
  const change = short ? '' : `, ${comparison(current, previous, against)}`;
  const { inPeriod, returning } = report.visitors;
  const unsure = visitorsUncertain(report);
  const who = inPeriod > 0
    ? ` They came from ${plural(figure(inPeriod, unsure), 'visitor')}${returning > 0 ? `, ${figure(returning, unsure)} of them returning` : ''}.`
    : '';
  return `${plural(figure(current, short), 'report')} came in through the bug collection widget${change}.${who}`;
}

/**
 * A ranking said of a read cut short holds only among the reports read: the
 * oldest left out could change who leads (Codex review round 3).
 */
const among = (report: ReportCollection, sentence: string) => (report.truncated ? `Among the reports read, ${sentence}` : sentence);

/** The widget that brought in the most, and its busiest site. */
export function collectionWidgetsTakeaway(report: ReportCollection): string | null {
  const top = report.widgets[0];
  const short = report.truncated;
  if (!top || top.opened.current === 0 || report.widgets.filter((widget) => widget.opened.current > 0).length < 2) {
    const site = top?.sites[0];
    if (!top || !site || top.opened.current === 0) return null;
    // The top site, by its own count: the busiest, not necessarily most of them (z.ai review round 3).
    return among(report, `${figure(site.reports, short)} came from ${site.host}.`);
  }
  const site = top.sites[0] ? `, ${figure(top.sites[0].reports, short)} of them from ${top.sites[0].host}` : '';
  return among(report, `${top.project.name}'s widget brought in the most: ${figure(top.opened.current, short)} of ${figure(report.opened.current, short)}${site}.`);
}

/** The page that drew the most reports. */
export function collectionPagesTakeaway(report: ReportCollection): string | null {
  const top = report.pages[0];
  return top ? among(report, `${top.host}${top.path} drew the most: ${figure(top.reports, report.truncated)}.`) : null;
}

/** Who reported the most, when someone reported more than once. */
export function visitorsTakeaway(report: ReportCollection): string | null {
  const top = report.visitors.list[0];
  const unsure = visitorsUncertain(report);
  const back = (n: number) => (unsure ? `At least ${n}` : String(n));
  const returning = report.visitors.returning;
  if (!top || top.current < 2) return returning > 0 ? `${back(returning)} ${returning === 1 ? 'visitor' : 'visitors'} came back.` : null;
  const again = returning > 0 ? ` ${back(returning)} ${returning === 1 ? 'visitor' : 'visitors'} had reported before.` : '';
  // Either cut can hide someone who reported more: the lead holds only among those the reports read can name.
  const lead = `${visitorLabel(top)} reported the most: ${figure(top.current, unsure)} in this period.`;
  return `${unsure ? `Of the visitors the reports read can name, ${lead}` : lead}${again}`;
}

/** Where the period's reports went. */
export function outcomesTakeaway(report: ReportCollection): string | null {
  const { intake, board, closed, archived } = report.outcomes;
  const total = intake + board + closed + archived;
  if (total === 0) return null;
  const hours = report.outcomes.medianHoursToClose;
  const typical = hours === null ? '' : `, typically in ${formatDuration(hours * 3600)}`;
  const short = report.truncated;
  const waiting = intake > 0 ? ` ${plural(figure(intake, short), 'report')} still ${intake === 1 && !short ? 'waits' : 'wait'} in intake.` : '';
  return `${among(report, `${figure(closed, short)} of ${plural(figure(total, short), 'report')} closed${typical}.`)}${waiting}`;
}
